import {
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { firestore } from 'services/Firebase';

type ActiveEventPuzzles = {
  event: QueryDocumentSnapshot<DocumentData>;
  puzzles: string[];
};

type CollectEventPuzzlesOptions = {
  onlyPublished?: boolean;
};

const collectEventPuzzles = async (options?: CollectEventPuzzlesOptions) => {
  const currentTimestamp = Timestamp.now();

  const filters = [];
  if (options?.onlyPublished) {
    filters.push(where('published', '==', true));
  }
  filters.push(where('endDate', '>=', currentTimestamp));

  const activeGameEventsQuery = query(collection(firestore, 'gameEvents'), ...filters);
  const activeGameEvents = await getDocs(activeGameEventsQuery);

  return await Promise.all(
    activeGameEvents?.docs?.map(async (event) => {
      const levelsQuery = query(collection(firestore, `gameEvents/${event.id}/levels`));
      const levels = await getDocs(levelsQuery);

      const levelPuzzles = await Promise.all(
        levels.docs.map((level) => {
          const levelPuzzleQuery = query(
            collection(firestore, `gameEvents/${event.id}/levels/${level.id}/puzzles`),
          );
          return getDocs(levelPuzzleQuery);
        }),
      );

      return {
        event: event,
        puzzles: levelPuzzles.map((levelPuzzle) => levelPuzzle.docs.map((doc) => doc.id)).flat(),
      };
    }),
  );
};

export const getActiveEventsPuzzlesParticipateIn = async (
  puzzleIds: string[] | string,
  activeEvents?: ActiveEventPuzzles[],
) => {
  if (!Array.isArray(puzzleIds)) puzzleIds = [puzzleIds];
  const activeEventPuzzles = activeEvents ?? (await collectEventPuzzles({ onlyPublished: true }));

  return activeEventPuzzles.filter((activeEventWithPuzzles) =>
    activeEventWithPuzzles.puzzles.some((activeEventPuzzleId) =>
      puzzleIds.includes(activeEventPuzzleId),
    ),
  );
};

export const getEventsPuzzlesParticipateIn = async (puzzleIds: string[] | string) => {
  if (!Array.isArray(puzzleIds)) puzzleIds = [puzzleIds];
  const eventPuzzles = await collectEventPuzzles();

  return eventPuzzles.filter((eventWithPuzzles) =>
    eventWithPuzzles.puzzles.some((eventPuzzleId) => puzzleIds.includes(eventPuzzleId)),
  );
};

export const getActiveEventsPuzzlesParticipateInByCreator = async (creatorId: string) => {
  const creatorPuzzlesRef = query(
    collection(firestore, 'puzzles'),
    where('Publisher', '==', creatorId),
    where('status', '==', 'approved'),
  );

  const creatorPuzzlesDocs = await getDocs(creatorPuzzlesRef);
  const activeEventPuzzles = await collectEventPuzzles({ onlyPublished: true });

  return await getActiveEventsPuzzlesParticipateIn(
    creatorPuzzlesDocs.docs.map((doc) => doc.id),
    activeEventPuzzles,
  );
};
